import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IApiResponse } from '@core/interfaces/api-response.interface';
import { IUser } from '@core/interfaces/user.interface';
import { EventTrackingService } from '@core/services/event-tracking.service';
import { SnackbarService } from '@core/services/snackbar.service';
import { UserService } from '@core/services/user.service';
import { EEventType } from '@enums/event-tracking.enum';
import { ComponentStore } from '@ngrx/component-store';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import {
  ISocialContentFile,
  ISocialContentFilesDeleteParams,
  ISocialContentFilesFetchParams,
} from './social-content-files.interface';
import { SocialContentFilesService } from './social-content-files.service';

interface ISocialContentFilesState {
  createdByUsers: Map<string, IUser>;
  error: string | null;
  files: ISocialContentFile[];
  loading: boolean;
}

const initialState: ISocialContentFilesState = {
  createdByUsers: new Map<string, IUser>(),
  error: null,
  files: [],
  loading: false,
};

@Injectable()
export class SocialContentFilesStore extends ComponentStore<ISocialContentFilesState> {
  public readonly createdByUsers$ = this.selectSignal((state) => state.createdByUsers);
  public readonly files$ = this.selectSignal((state) => state.files);
  public readonly loading$ = this.selectSignal((state) => state.loading);
  public readonly error$ = this.selectSignal((state) => state.error);

  public readonly addFile = this.updater((state, file: ISocialContentFile) => ({
    ...state,
    files: [...state.files, file],
  }));

  public readonly fetchFiles = this.effect((params$: Observable<ISocialContentFilesFetchParams>) =>
    params$.pipe(
      tap(() => this.patchState({ error: null, loading: true })),
      switchMap((params) =>
        this.socialContentFilesService.getFiles(params).pipe(
          switchMap((response: IApiResponse<ISocialContentFile[]>) => {
            const files = response.data ?? [];

            return this.fetchCreatedByUsers(files, params.creatorBriefingMode).pipe(
              map((createdByUsers) => ({ createdByUsers, files })),
            );
          }),
          tap(({ createdByUsers, files }) => {
            this.patchState({ createdByUsers, files, loading: false });
          }),
          catchError(() => {
            this.patchState({ error: 'Failed to load social content files', loading: false });

            return EMPTY;
          }),
        ),
      ),
    ),
  );

  public readonly deleteFile = this.effect((params$: Observable<ISocialContentFilesDeleteParams>) =>
    params$.pipe(
      tap(() => this.patchState({ error: null })),
      switchMap((params) =>
        this.socialContentFilesService.deleteFile(params).pipe(
          tap(() => {
            const deletedFile = this.get().files.find((file) => file.id === params.socialContentFileId);

            this.patchState((state) => ({
              files: state.files.filter((file) => file.id !== params.socialContentFileId),
            }));

            this.eventTrackingService.track(EEventType.campaignAnalyticsAttachmentDeleted, {
              campaignId: deletedFile?.campaignId,
              isBriefing: params.creatorBriefingMode,
              socialContentId: deletedFile?.socialContentId,
            });
          }),
          catchError(() => {
            this.patchState({ error: 'Failed to delete social content file' });
            this.snackbarService.openError(this.translate.instant('components.socialContentFiles.couldNotDeleteFile'));

            return EMPTY;
          }),
        ),
      ),
    ),
  );

  constructor(
    private readonly socialContentFilesService: SocialContentFilesService,
    private readonly snackbarService: SnackbarService,
    private readonly translate: TranslateService,
    private readonly userService: UserService,
    private readonly eventTrackingService: EventTrackingService,
  ) {
    super(initialState);
  }

  private fetchCreatedByUsers(
    files: ISocialContentFile[],
    creatorBriefingMode: boolean,
  ): Observable<Map<string, IUser>> {
    if (creatorBriefingMode) {
      return of(new Map<string, IUser>());
    }

    const createdByUserIds = Array.from(
      new Set(files.map((file) => file.createdBy).filter((createdBy): createdBy is string => !!createdBy)),
    );

    if (!createdByUserIds.length) {
      return of(new Map<string, IUser>());
    }

    return this.userService.getUsers(new HttpParams().set('id__in', createdByUserIds.join(','))).pipe(
      map((response: IApiResponse<IUser[]>) => {
        const users = response.data ?? [];

        return new Map(users.map((user) => [user.id, user] satisfies [string, IUser]));
      }),
      catchError(() => of(new Map<string, IUser>())),
    );
  }
}
